/**
 * ModulePicker — Combobox picker for selecting a software factory module.
 *
 * Uses EntityPickerBase for the standard autocomplete/combobox pattern.
 * Works in both form contexts (value/onChange) and filter contexts.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Boxes, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase, BidiText } from '../../../shared/components/common';
import { getEntityUrl } from '../../../shared/utils/entityUrls';
import { filterAndSortBySearch } from '../../../shared/utils/searchScore';
import { useModules } from '../hooks/useModuleQueries';
import { ModuleIcon } from './ModuleIcon';
import type { SfModule } from '../types';

interface ModulePickerProps {
  /** Currently selected module ID (empty string or null for none) */
  value: string | null;
  /** Called when a module is selected or cleared */
  onChange: (moduleId: string | null) => void;
  /** Label above the picker */
  label?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Show required indicator */
  required?: boolean;
  /** Show "None" clear option (default: true) */
  allowClear?: boolean;
  /** Override placeholder text (default: "No Module") */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Show selected module as a clickable link to its detail page */
  linkToEntity?: boolean;
}

export function ModulePicker({
  value,
  onChange,
  label,
  disabled,
  required,
  allowClear = true,
  placeholder: placeholderOverride,
  className,
  linkToEntity = false,
}: ModulePickerProps) {
  const { t } = useTranslation(['softwareFactory', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: modulesData } = useModules();
  const allModules = useMemo(() => modulesData?.items ?? [], [modulesData?.items]);

  const filteredModules = useMemo(
    () => filterAndSortBySearch(allModules, search, (m) => [m.name, m.slug].filter(Boolean).join(' ')),
    [allModules, search],
  );

  return (
    <EntityPickerBase<SfModule>
      value={value || ''}
      items={filteredModules}
      getItemLabel={(m) => m.name}
      renderItem={(m, isSelected) => (
        <div className="flex items-center gap-2 w-full">
          <Check className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
          <ModuleIcon iconName={m.icon} color={m.color} className="h-4 w-4" />
          <BidiText className="truncate">{m.name}</BidiText>
        </div>
      )}
      label={label}
      placeholder={placeholderOverride ?? t('softwareFactory:initiatives.noModule')}
      searchPlaceholder={t('softwareFactory:modules.searchPlaceholder')}
      emptyMessage={t('softwareFactory:modules.emptyTitle')}
      disabled={disabled}
      required={required}
      className={className}
      search={search}
      onSearchChange={setSearch}
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearch('');
      }}
      onSelect={(id) => {
        onChange(id);
        setOpen(false);
      }}
      allowClear={allowClear}
      onClear={() => {
        onChange(null);
        setOpen(false);
      }}
      selectedEntityUrl={linkToEntity && value
        ? getEntityUrl({ entityType: 'sf_modules', entityId: value })
        : undefined}
      entityIcon={linkToEntity ? Boxes : undefined}
    />
  );
}
